import type { StockData } from '@/types/chart';
import { OhlcvIndex } from '@/types/chart';
import { ShapeType } from '@/types/commontypes';
import type { RenderNode } from '@/types/commontypes';
import type { Coordinate } from '@/types/properties';
import type { XAccessor } from '@/hooks/use-candle-data';
import { getTimeframeMilliseconds } from './duration';

/**
 * Find the closest candle index for a given timestamp using binary search
 */
function findClosestCandleIndex(timestamp: number, candleData: StockData[]): number {
  if (!candleData || candleData.length === 0) return 0;

  let left = 0;
  let right = candleData.length - 1;

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const candleTime = candleData[mid].ohlcv[OhlcvIndex.TIME];

    if (candleTime === timestamp) return mid;
    if (candleTime < timestamp) left = mid + 1;
    else right = mid - 1;
  }

  // Return the closest index
  if (left >= candleData.length) return candleData.length - 1;
  if (right < 0) return 0;

  const leftDiff = Math.abs(candleData[left].ohlcv[OhlcvIndex.TIME] - timestamp);
  const rightDiff = Math.abs(candleData[right].ohlcv[OhlcvIndex.TIME] - timestamp);

  return leftDiff < rightDiff ? left : right;
}

/**
 * find candle by ScaleProvider index
 * if xAccessor is provided, find candle by ScaleProvider index
 * if xAccessor is not provided, find candle by array index
 */
function findCandleByScaleIndex(
  scaleIndex: number,
  candleData: StockData[],
  xAccessor?: XAccessor,
): StockData | undefined {
  if (!candleData || candleData.length === 0) return undefined;

  if (xAccessor) {
    // find candle by ScaleProvider index
    return candleData.find((d: StockData) => xAccessor(d) === scaleIndex);
  } else {
    // find candle by array index
    const arrayIndex = Math.max(0, Math.min(Math.floor(scaleIndex), candleData.length - 1));
    return candleData[arrayIndex];
  }
}

/**
 * convert index to timestamp (supports future index)
 * @param scaleIndex - ScaleProvider index
 * @param candleData - candle data array
 * @param timeframe - timeframe (e.g., '1m', '5m', '1h', '1d')
 * @param xAccessor - ScaleProvider xAccessor (optional)
 */
function getTimestampForIndex(
  scaleIndex: number,
  candleData: StockData[],
  timeframe: string,
  xAccessor?: XAccessor,
): number {
  if (!candleData || candleData.length === 0) return 0;

  const candle = findCandleByScaleIndex(scaleIndex, candleData, xAccessor);
  if (candle) {
    return candle.ohlcv[OhlcvIndex.TIME];
  }

  const interval = getTimeframeMilliseconds(timeframe);

  // if candles are not found (future index): estimate based on the last candle
  const lastCandle = candleData[candleData.length - 1];
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const lastIndex = xAccessor ? (xAccessor(lastCandle) as number) : candleData.length - 1;
  const indexDiff = scaleIndex - lastIndex;

  if (indexDiff > 0) {
    // future index: last candle timestamp + (index difference × interval)
    return lastCandle.ohlcv[OhlcvIndex.TIME] + indexDiff * interval;
  }

  // past index (negative index etc.): first candle based estimation
  const firstCandle = candleData[0];
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const firstIndex = xAccessor ? (xAccessor(firstCandle) as number) : 0;
  const pastIndexDiff = scaleIndex - firstIndex;

  if (pastIndexDiff < 0) {
    return firstCandle.ohlcv[OhlcvIndex.TIME] + pastIndexDiff * interval;
  }

  // fallback
  return lastCandle.ohlcv[OhlcvIndex.TIME];
}

/**
 * Convert shape's index-based coordinates to timestamp-based coordinates
 * for database storage
 *
 * @param shape - The shape object with index-based coordinates
 * @param candleData - Array of candle data with ohlcv information
 * @param timeframe - Timeframe string (e.g., '1m', '5m', '1h', '1d')
 * @param xAccessor - Optional xAccessor from ChartCanvasContext for ScaleProvider index lookup
 * @returns Array of coordinates in format [[timestamp, price], ...] or [[timestamp]] or [[price]]
 */
export function shapeToCoordinates(
  shape: RenderNode,
  candleData: StockData[],
  timeframe: string,
  xAccessor?: XAccessor,
): Coordinate[] {
  if (!candleData || candleData.length === 0) return [];

  // Helper function to get timestamp from scale index (미래 인덱스 지원)
  const getTimestamp = (scaleIndex: number): number => {
    return getTimestampForIndex(scaleIndex, candleData, timeframe, xAccessor);
  };

  switch (shape.type) {
    // Points array format: ElliottImpulse, ElliottCorrectionABC, XABCDPattern, Path
    case ShapeType.ELLIOTT_IMPULSE_12345:
    case ShapeType.ELLIOTT_CORRECTION_ABC:
    case ShapeType.XABCD_PATTERN:
    case ShapeType.PATH: {
      const { points } = shape;
      if (points !== undefined && points.length > 0) {
        return points.map(point => [getTimestamp(point[0]), point[1]]);
      }
      return [];
    }

    // start/end format: TrendLine, Ray, InfoLine, Rectangle, FixedRangeVolumeProfile
    case ShapeType.TREND_LINE:
    case ShapeType.RAY:
    case ShapeType.INFO_LINE:
    case ShapeType.RECTANGLE:
    case ShapeType.FIXED_RANGE_VOLUME_PROFILE: {
      const { start, end } = shape;
      if (start !== undefined && end !== undefined) {
        return [
          [getTimestamp(start[0]), start[1]],
          [getTimestamp(end[0]), end[1]],
        ];
      }
      return [];
    }

    // startXY/endXY/endXY2 format: FibChannel, ParallelChannel, Triangle, GannBox, GannFan
    case ShapeType.FIB_CHANNEL:
    case ShapeType.PARALLEL_CHANNEL:
    case ShapeType.TRIANGLE:
    case ShapeType.GANN_BOX:
    case ShapeType.GANN_FAN: {
      const { startXY, endXY, endXY2 } = shape;
      if (startXY !== undefined && endXY !== undefined) {
        const coords: Coordinate[] = [
          [getTimestamp(startXY[0]), startXY[1]],
          [getTimestamp(endXY[0]), endXY[1]],
        ];
        if (endXY2 !== undefined) {
          coords.push([getTimestamp(endXY2[0]), endXY2[1]]);
        }
        return coords;
      }
      return [];
    }

    // x1/y1/x2/y2 format: FibRetracement
    case ShapeType.FIB_RETRACEMENT: {
      const { x1, y1, x2, y2 } = shape;
      if (x1 !== undefined && y1 !== undefined && x2 !== undefined && y2 !== undefined) {
        return [
          [getTimestamp(x1), y1],
          [getTimestamp(x2), y2],
        ];
      }
      return [];
    }

    // position format: Text
    case ShapeType.TEXT: {
      const { position } = shape;
      if (position !== undefined) {
        return [[getTimestamp(Math.trunc(position[0])), position[1]]];
      }
      return [];
    }

    // AnchoredVolumeProfile: start only
    case ShapeType.ANCHORED_VOLUME_PROFILE: {
      const { start } = shape;
      if (start !== undefined) {
        return [[getTimestamp(start[0]), start[1]]];
      }
      return [];
    }

    // VertLine: xValue only
    case ShapeType.VERT_LINE: {
      const { xValue } = shape;
      if (xValue !== undefined) {
        return [[getTimestamp(xValue), 0]];
      }
      return [];
    }

    // HorzLine: yValue only
    case ShapeType.HORZ_LINE: {
      const { yValue } = shape;
      if (yValue !== undefined) {
        return [[0, yValue]];
      }
      return [];
    }

    // OrderLine: yValue only
    case ShapeType.ORDER_LINE: {
      const { yValue } = shape;
      if (yValue !== undefined) {
        return [[0, yValue]];
      }
      return [];
    }

    // Measure: not stored
    case ShapeType.MEASURE:
      return [];

    default:
      return [];
  }
}

/**
 * convert timestamp to index (supports future timestamp)
 * @param timestamp - timestamp value
 * @param candleData - candle data array
 * @param timeframe - timeframe (e.g., '1m', '5m', '1h', '1d')
 * @param xAccessor - ScaleProvider xAccessor (optional)
 */
function getScaleIndexForTimestamp(
  timestamp: number,
  candleData: StockData[],
  timeframe: string,
  xAccessor?: XAccessor,
): number {
  if (!candleData || candleData.length === 0) return 0;

  const lastCandle = candleData[candleData.length - 1];
  const lastTimestamp = lastCandle.ohlcv[OhlcvIndex.TIME];
  const firstCandle = candleData[0];
  const firstTimestamp = firstCandle.ohlcv[OhlcvIndex.TIME];

  const interval = getTimeframeMilliseconds(timeframe);

  // future timestamp: last candle index + (timestamp diff / interval)
  if (timestamp > lastTimestamp) {
    const indexDiff = Math.round((timestamp - lastTimestamp) / interval);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const lastIndex = xAccessor ? (xAccessor(lastCandle) as number) : candleData.length - 1;
    return lastIndex + indexDiff;
  }

  // past timestamp: first candle index + (timestamp diff / interval)
  if (timestamp < firstTimestamp) {
    const indexDiff = Math.round((timestamp - firstTimestamp) / interval);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const firstIndex = xAccessor ? (xAccessor(firstCandle) as number) : 0;
    return firstIndex + indexDiff;
  }

  // timestamp within candle range: original logic
  const arrayIndex = findClosestCandleIndex(timestamp, candleData);
  const candle = candleData[arrayIndex];

  if (xAccessor && candle) {
    const scaleIndex = xAccessor(candle);
    return typeof scaleIndex === 'number' ? scaleIndex : arrayIndex;
  }

  return arrayIndex;
}

/**
 * Convert timestamp-based coordinates back to index-based coordinates
 * for rendering
 *
 * @param coordinates - Array of coordinates [[timestamp, price], ...]
 * @param candleData - Array of candle data with ohlcv information
 * @param type - Type of shape to determine which format to use
 * @param timeframe - Timeframe string (e.g., '1m', '5m', '1h', '1d')
 * @param xAccessor - Optional xAccessor from ChartCanvasContext for ScaleProvider index lookup
 * @returns Object with index-based coordinate properties
 */
export function coordinatesToShape(
  coordinates: Coordinate[],
  candleData: StockData[],
  type: ShapeType,
  timeframe: string,
  xAccessor?: XAccessor,
): Record<string, unknown> {
  if (!coordinates || coordinates.length === 0) {
    return {};
  }

  // candleData가 아직 로드되지 않은 경우, timestamp를 인덱스로 변환할 수 없으므로
  // coordinates의 timestamp를 임시 인덱스(0, 1, 2...)로 사용하여 좌표 필드를 생성한다.
  // ShapeManager의 candleData 변경 useEffect에서 정확한 인덱스로 재계산됨.
  const hasCandleData = candleData && candleData.length > 0;

  // Helper to get scale index from timestamp.
  // candleData가 없으면 timestamp를 그대로 반환 (임시값, 이후 ShapeManager에서 재계산)
  const getIndex = (timestamp: number) =>
    hasCandleData ? getScaleIndexForTimestamp(timestamp, candleData, timeframe, xAccessor) : timestamp;

  // TrendLine, Ray, InfoLine - use start/end format
  if (type === ShapeType.TREND_LINE || type === ShapeType.RAY || type === ShapeType.INFO_LINE) {
    if (coordinates.length >= 2) {
      const startIndex = getIndex(coordinates[0][0]);
      const endIndex = getIndex(coordinates[1][0]);
      return {
        start: [startIndex, coordinates[0][1]],
        end: [endIndex, coordinates[1][1]],
      };
    }
  }

  // FibRetracement - use x1/y1/x2/y2 format
  if (type === ShapeType.FIB_RETRACEMENT) {
    if (coordinates.length >= 2) {
      const x1 = getIndex(coordinates[0][0]);
      const x2 = getIndex(coordinates[1][0]);
      return {
        x1,
        y1: coordinates[0][1],
        x2,
        y2: coordinates[1][1],
      };
    }
  }

  // FibChannel, ParallelChannel, Triangle, GannBox - use startXY/endXY/endXY2 format
  if (
    type === ShapeType.FIB_CHANNEL ||
    type === ShapeType.PARALLEL_CHANNEL ||
    type === ShapeType.TRIANGLE ||
    type === ShapeType.GANN_BOX
  ) {
    if (coordinates.length >= 2) {
      const startIndex = getIndex(coordinates[0][0]);
      const endIndex = getIndex(coordinates[1][0]);
      const result: Record<string, unknown> = {
        startXY: [startIndex, coordinates[0][1]],
        endXY: [endIndex, coordinates[1][1]],
      };

      // Add third point if it exists
      if (coordinates.length >= 3) {
        const endIndex2 = getIndex(coordinates[2][0]);
        result.endXY2 = [endIndex2, coordinates[2][1]];
      }

      return result;
    }
  }

  // ElliottImpulse, ElliottCorrectionABC, XABCDPattern, Path - use points array
  if (
    type === ShapeType.ELLIOTT_IMPULSE_12345 ||
    type === ShapeType.ELLIOTT_CORRECTION_ABC ||
    type === ShapeType.XABCD_PATTERN ||
    type === ShapeType.PATH
  ) {
    if (coordinates.length >= 2) {
      const points = coordinates.map(coord => {
        const pointIndex = getIndex(coord[0]);
        return [pointIndex, coord[1]];
      });
      return { points };
    }
  }

  // Rectangle - use start/end
  if (type === ShapeType.RECTANGLE) {
    if (coordinates.length >= 2) {
      const startIndex = getIndex(coordinates[0][0]);
      const endIndex = getIndex(coordinates[1][0]);
      return {
        start: [startIndex, coordinates[0][1]],
        end: [endIndex, coordinates[1][1]],
      };
    }
  }

  // Text - use position format
  if (type === ShapeType.TEXT) {
    if (coordinates.length >= 1) {
      const posIndex = getIndex(coordinates[0][0]);
      return {
        position: [posIndex, coordinates[0][1]],
      };
    }
  }

  // VertLine - xValue + start/end (InteractiveVerticalLine 렌더링에 start/end 필요)
  if (type === ShapeType.VERT_LINE) {
    if (coordinates.length >= 1) {
      const xIndex = getIndex(coordinates[0][0]);
      return {
        xValue: xIndex,
        start: [xIndex, 0],
        end: [xIndex, 0],
      };
    }
  }

  // HorzLine - yValue only (start/end는 생성 시 xScale.domain()에서 설정됨)
  // 복원 시 start/end가 없으면 TrendLine.tsx에서 yValue 기반 폴백 처리
  if (type === ShapeType.HORZ_LINE) {
    if (coordinates.length >= 1) {
      return {
        yValue: coordinates[0][1],
      };
    }
  }

  // GannFan - use startXY/endXY format (same as GannBox)
  if (type === ShapeType.GANN_FAN) {
    if (coordinates.length >= 2) {
      const startIndex = getIndex(coordinates[0][0]);
      const endIndex = getIndex(coordinates[1][0]);
      return {
        startXY: [startIndex, coordinates[0][1]],
        endXY: [endIndex, coordinates[1][1]],
      };
    }
  }

  // FixedRangeVolumeProfile - use start/end format (same as Rectangle)
  if (type === ShapeType.FIXED_RANGE_VOLUME_PROFILE) {
    if (coordinates.length >= 2) {
      const startIndex = getIndex(coordinates[0][0]);
      const endIndex = getIndex(coordinates[1][0]);
      return {
        start: [startIndex, coordinates[0][1]],
        end: [endIndex, coordinates[1][1]],
      };
    }
  }

  // AnchoredVolumeProfile - use start only (end is always latest candle)
  if (type === ShapeType.ANCHORED_VOLUME_PROFILE) {
    if (coordinates.length >= 1) {
      const startIndex = getIndex(coordinates[0][0]);
      return {
        start: [startIndex, coordinates[0][1]],
      };
    }
  }

  // OrderLine - yValue only (stored as [0, yValue])
  if (type === ShapeType.ORDER_LINE) {
    if (coordinates.length >= 1) {
      return {
        yValue: coordinates[0][1],
      };
    }
  }

  return {};
}

/**
 * Validate if coordinates are properly formatted
 */
export function validateCoordinates(coordinates: Coordinate[]): boolean {
  if (!Array.isArray(coordinates)) return false;
  if (coordinates.length === 0) return false;

  return coordinates.every(
    coord => Array.isArray(coord) && coord.length === 2 && coord.every(val => typeof val === 'number' && !isNaN(val)),
  );
}
